import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchTicketById, fetchChildTickets, createTicketReply, updateTicket, fetchSupportMembers, fetchTicketPresets, pb } from '../api/pocketbase';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaPaperclip, FaReply, FaTimes, FaUserPlus, FaFileAlt, FaCircle } from 'react-icons/fa';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import toast, { Toaster } from 'react-hot-toast';
import { getTicketAssignedTemplate } from '../templates/ticketAssigned';
import { sendEmail } from '../utils/emailService';

const TicketDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [childTickets, setChildTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [replyContent, setReplyContent] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const [supportMembers, setSupportMembers] = useState([]);
    const [selectedSupportMember, setSelectedSupportMember] = useState("");
    const [ticketPresets, setTicketPresets] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);

    useEffect(() => {
        const loadTicket = async () => {
            try {
                const data = await fetchTicketById(id);
                setTicket(data);
                setSelectedSupportMember(data.SupportMember || "");
                
                // Fetch conversation history
                const children = await fetchChildTickets(data.id);
                setChildTickets(children);
            } catch (error) {
                console.error("Error loading ticket:", error);
            } finally {
                setIsLoading(false);
            }
        };
        if (id) {
            loadTicket();
        }
    }, [id]);

    useEffect(() => {
        const loadSupportMembers = async () => {
            try {
                const contacts = await fetchSupportMembers();
                setSupportMembers(contacts);
            } catch (error) {
                console.error("Error loading support members:", error);
            }
        };
        loadSupportMembers();
    }, []);

    useEffect(() => {
        const loadPresets = async () => {
            const presets = await fetchTicketPresets();
            setTicketPresets(presets);
        };
        loadPresets();
    }, []);

    const getFileUrl = (record, filename) => {
        const file = Array.isArray(filename) ? filename[0] : filename;
        if (!file) return "";
        return pb.files.getUrl(record, file);
    };

    const handleOpenImageModal = (record, filename) => {
        const url = getFileUrl(record, filename);
        setImageUrl(url);
        setImageModalOpen(true);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    // Replace editor content with selected preset (not append)
    const insertTemplate = (content) => {
        setReplyContent(content);
        setShowTemplates(false);
    };

    const handleSendReply = async () => {
        if (!replyContent || replyContent.trim() === '<p><br></p>') {
            toast.error("Please enter a message");
            return;
        }

        setIsSending(true);
        try {
            const authStore = JSON.parse(localStorage.getItem("pocketbase_auth") || "null");
            const user = authStore?.record;
            
            // Create FormData for the new reply entry
            const formData = new FormData();
            formData.append('Communication', replyContent);
            formData.append('reference', ticket.id);
            formData.append('Initiator', user ? `${user.Name} ${user.Surname}` : "Support Member");
            formData.append('CompanyID', ticket.CompanyID);
            // Mark this reply as coming from support
            formData.append('is_support_reply', 'true');

            if (selectedFile) {
                formData.append('Screenshot', selectedFile);
            }

            // 1. Create the reply
            await createTicketReply(formData);
            
            // 2. Update the Parent Ticket: set SupportMember + flag is_support_reply so
            //    the ticket list knows the last reply was from support
            await updateTicket(ticket.id, {
                SupportMember: user?.id || ticket.SupportMember,
                is_support_reply: true,
            });

            toast.success("Reply sent successfully");
            setReplyContent("");
            setSelectedFile(null);
            if(fileInputRef.current) fileInputRef.current.value = "";
            
            // Refresh conversation & Ticket Data
            const children = await fetchChildTickets(ticket.id);
            setChildTickets(children);
            
            const updatedTicket = await fetchTicketById(id);
            setTicket(updatedTicket);

        } catch (error) {
            console.error("Error sending reply:", error);
            toast.error("Failed to send reply");
        } finally {
            setIsSending(false);
        }
    };



    const handleAssignSupportMember = async () => {
        if (!selectedSupportMember) {
            toast.error("Please select a support member");
            return;
        }
        
        try {
            await updateTicket(id, { SupportMember: selectedSupportMember });
            
            // Send Email Notification
            const member = supportMembers.find(m => m.id === selectedSupportMember);
            if (member && member.email) {
                const companyName = ticket.expand?.CompanyID?.CompanyName || "Unknown Company";
                const ticketLink = window.location.href; // Direct link to current page

                const emailHtml = getTicketAssignedTemplate(
                    `${member.Name} ${member.Surname}`,
                    ticket.reference || ticket.id,
                    companyName,
                    ticketLink
                );

                // We don't await this to prevent blocking UI if email service is slow/fails
                sendEmail(member.email, `New Ticket Assignment - #${ticket.reference || ticket.id}`, emailHtml)
                    .catch(err => console.error("Failed to trigger email notification", err));
            }

            toast.success("Support member assigned successfully");
            
            // Refresh ticket data
            const updatedTicket = await fetchTicketById(id);
            setTicket(updatedTicket);
        } catch (error) {
            console.error("Error assigning support member:", error);
            toast.error("Failed to assign support member");
        }
    };

    // ... (rest of loading/not found checks are same, replacing up to return)

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-xl font-semibold text-gray-600">Loading ticket details...</div>
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
                <div className="text-xl font-semibold text-gray-600 mb-4">Ticket not found</div>
                <button 
                    onClick={() => navigate('/tickets')}
                    className="px-4 py-2 bg-[#90c740] text-white rounded hover:bg-[#7ebd2e] transition-colors"
                >
                    Back to Tickets
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Toaster position="top-right" />
            
            {/* Header */}
            <header className="flex flex-wrap items-start gap-3 px-4 sm:px-6 md:px-10 py-4 sm:py-6 bg-white shadow-sm rounded-bl-3xl rounded-br-3xl mb-6">
                <button 
                    onClick={() => navigate('/tickets')}
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <FaArrowLeft size={20} />
                </button>
                <div className="min-w-0 flex-1">
                    <h1 className="break-words text-2xl font-bold text-gray-900">
                        Ticket # {ticket.reference || ticket.id}
                    </h1>
                    <p className="break-words text-sm text-gray-500">
                        {ticket.expand?.CompanyID?.CompanyName || "Unknown Company"}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-auto">
                    {ticket.is_support_reply === true && (
                        <span className="px-4 py-2 text-sm font-bold rounded-full bg-blue-100 text-blue-700">
                            Replied
                        </span>
                    )}
                    <span className={`px-4 py-2 text-sm font-bold rounded-full ${
                        !ticket.TicketClose 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                    }`}>
                        {!ticket.TicketClose ? 'Open' : 'Closed'}
                    </span>
                </div>
            </header>

            {/* Green Banner for unhandled tickets */}
            {ticket.is_support_reply !== true && !ticket.TicketClose && (
                <div className="bg-[#90c740] text-white px-4 sm:px-6 md:px-10 py-3 font-extrabold flex items-center justify-center gap-3 shadow-inner text-center">
                    <FaCircle className="text-[10px] animate-pulse" />
                    <span className="tracking-wide uppercase">Needs Reply - Customer is waiting for a response</span>
                </div>
            )}

            {/* Main Content */}
            <main className="flex-1 px-2 sm:px-4 md:px-10 pb-6 sm:pb-10 max-w-7xl mx-auto w-full space-y-6">
                
                {/* 1. Main Ticket Info */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col gap-4 text-sm text-gray-600 lg:flex-row lg:items-start lg:justify-between">
                         <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-6">
                            <div className="flex items-center gap-2">
                                <FaUser className="text-gray-400" />
                                <span>Initiator: <strong>{ticket.Initiator || "N/A"}</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaCalendarAlt className="text-gray-400" />
                                <span>Created: <strong>{new Date(ticket.created).toLocaleString()}</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaCalendarAlt className="text-gray-400" />
                                <span>Updated: <strong>{new Date(ticket.updated).toLocaleString()}</strong></span>
                            </div>
                         </div>
                         <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Original Request
                         </div>
                    </div>
                    <div className="p-4 sm:p-8">
                        <div className="flex flex-col gap-6 lg:flex-row">
                            <div 
                                className="prose max-w-none text-gray-800 flex-1 break-words overflow-hidden"
                                dangerouslySetInnerHTML={{ __html: ticket.Communication || "<p>No content.</p>" }}
                            />
                            {ticket.Screenshot && ticket.Screenshot.length > 0 && (
                                <div className="flex-shrink-0">
                                    <img
                                        src={getFileUrl(ticket, ticket.Screenshot)}
                                        alt="Ticket Screenshot"
                                        className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => handleOpenImageModal(ticket, ticket.Screenshot)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2. Assign Support Member Section */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center gap-2">
                        <FaUserPlus className="text-gray-600" />
                        <h3 className="font-semibold text-gray-800">Assign Support Member</h3>
                    </div>
                    <div className="p-4 sm:p-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                            <div className="flex-1 min-w-0">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Support Member
                                </label>
                                <select
                                    value={selectedSupportMember}
                                    onChange={(e) => setSelectedSupportMember(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#90c740] focus:border-transparent"
                                >
                                    <option value="">-- Select a support member --</option>
                                    {supportMembers.map((member) => (
                                        <option key={member.id} value={member.id}>
                                            {member.Name} {member.Surname} ({member.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={handleAssignSupportMember}
                                className="w-full bg-[#90c740] hover:bg-[#7ab030] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 sm:w-auto"
                            >
                                Assign
                            </button>
                        </div>
                        {ticket.SupportMember && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                                <strong>Currently Assigned:</strong> {ticket.expand?.SupportMember 
                                    ? `${ticket.expand.SupportMember.Name} ${ticket.expand.SupportMember.Surname}` 
                                    : ticket.SupportMember}
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. Conversation Thread (Child Tickets) */}
                {childTickets.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 px-2 text-gray-500 font-medium text-sm uppercase tracking-wide">
                            <FaReply /> Conversation History
                        </div>
                        {childTickets.map((child, index) => (
                            <div key={child.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 ml-0 md:ml-8 relative">
                                {/* Connector Line (Visual enhancement could be added here in CSS) */}
                                <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                                    <div className="font-semibold text-gray-800 break-words">
                                        {child.Initiator || "Support"}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                        {new Date(child.created).toLocaleString()}
                                    </div>
                                </div>
                                <div className="p-4 sm:p-6">
                                    <div className="flex flex-col gap-4 lg:flex-row">
                                        <div 
                                            className="prose max-w-none text-gray-800 text-sm flex-1 break-words overflow-hidden"
                                            dangerouslySetInnerHTML={{ __html: child.Communication }}
                                        />
                                        {child.Screenshot && child.Screenshot.length > 0 && (
                                            <div className="flex-shrink-0">
                                                <img
                                                    src={getFileUrl(child, child.Screenshot)}
                                                    alt="Reply Screenshot"
                                                    className="w-24 h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                                    onClick={() => handleOpenImageModal(child, child.Screenshot)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* 3. Reply Section */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200 font-bold text-gray-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <span>Post a Reply</span>
                        <div className="relative">
                            <button 
                                onClick={() => setShowTemplates(!showTemplates)}
                                className="text-sm font-normal text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 px-3 py-1 rounded-full border border-blue-200 transition-colors"
                            >
                                <FaFileAlt className="text-xs" /> Insert Template
                            </button>
                            {showTemplates && (
                                <div className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden">
                                    <div className="p-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Select a template
                                    </div>
                                    <div className="max-h-60 overflow-y-auto">
                                        {ticketPresets.length > 0 ? (
                                            ticketPresets.map(preset => (
                                                <button
                                                    key={preset.id}
                                                    onClick={() => insertTemplate(preset.preset)}
                                                    className="w-full text-left px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                                                >
                                                    <div className="font-medium text-gray-900 text-sm">{preset.preset_name}</div>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="px-4 py-6 text-center text-sm text-gray-500">
                                                No presets found. <br/>Add them in the Dynamics page.
                                            </div>
                                        )}
                                    </div>
                                    {/* Click outside listener is tricky here without a hook, but basic toggle works */}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-4 sm:p-6">
                        <ReactQuill 
                            theme="snow" 
                            value={replyContent} 
                            onChange={setReplyContent}
                            className="bg-white"
                            placeholder="Type your reply here..."
                        />

                        {/* File Upload Section */}
                        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                            <div className="flex flex-wrap items-center gap-3">
                                <input 
                                    type="file" 
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="reply-attachment"
                                />
                                <label 
                                    htmlFor="reply-attachment"
                                    className="flex items-center gap-2 text-gray-600 hover:text-[#90c740] cursor-pointer transition-colors text-sm font-medium"
                                >
                                    <FaPaperclip className="text-lg" />
                                    {selectedFile ? 'Change File' : 'Attach Screenshot'}
                                </label>
                                {selectedFile && (
                                    <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-700">
                                        <span className="max-w-[150px] truncate">{selectedFile.name}</span>
                                        <button 
                                            onClick={() => {
                                                setSelectedFile(null);
                                                if(fileInputRef.current) fileInputRef.current.value = "";
                                            }}
                                            className="text-gray-400 hover:text-red-500"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleSendReply}
                                disabled={isSending}
                                className={`w-full px-6 py-2 bg-[#90c740] text-white font-bold rounded shadow-md hover:bg-[#7ebd2e] transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-[#90c740] sm:w-auto ${isSending ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {isSending ? 'Sending...' : 'Send Reply'}
                            </button>
                        </div>
                    </div>
                </div>

            </main>

            {/* Image Modal */}
            {imageModalOpen && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
                    onClick={() => setImageModalOpen(false)}
                >
                    <div className="relative max-w-full max-h-full">
                        <img
                            src={imageUrl}
                            alt="Screenshot"
                            className="max-w-full max-h-[90vh] rounded shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button 
                            className="absolute top-3 right-3 rounded-full bg-black/50 px-3 py-1 text-white text-3xl leading-none hover:text-gray-300"
                            onClick={() => setImageModalOpen(false)}
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketDetailsPage;
